export const VAT_PATTERNS: Record<
  string,
  { pattern: RegExp; example: string; description: string }
> = {
  // European Union Countries (with VAT)
  AT: {
    pattern: /^ATU\d{8}$/,
    example: "ATU12345678",
    description: "Austria: 1 letter (U) + 8 digits",
  },
  BE: {
    pattern: /^BE[0-1]\d{9}$/,
    example: "BE0123456789",
    description:
      "Belgium: 2 digits + 9 digits (first digit after BE is 0 or 1)",
  },
  BG: {
    pattern: /^BG\d{9,10}$/,
    example: "BG123456789",
    description: "Bulgaria: 9 or 10 digits",
  },
  HR: {
    pattern: /^HR\d{11}$/,
    example: "HR12345678901",
    description: "Croatia: 11 digits",
  },
  CY: {
    pattern: /^CY\d{8}[A-Z]$/,
    example: "CY12345678X",
    description: "Cyprus: 8 digits + 1 letter",
  },
  CZ: {
    pattern: /^CZ\d{8,10}$/,
    example: "CZ12345678",
    description: "Czech Republic: 8, 9 or 10 digits",
  },
  DK: {
    pattern: /^DK\d{8}$/,
    example: "DK12345678",
    description: "Denmark: 8 digits",
  },
  EE: {
    pattern: /^EE10\d{9}$/,
    example: "EE10123456789",
    description: "Estonia: 11 digits starting with 10",
  },
  FI: {
    pattern: /^FI\d{8}$/,
    example: "FI12345678",
    description: "Finland: 8 digits",
  },
  FR: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "France: 2 characters + 9 digits (total 12 characters)",
  },
  DE: {
    pattern: /^DE\d{9}$/,
    example: "DE123456789",
    description: "Germany: 9 digits",
  },
  EL: {
    pattern: /^EL\d{9}$/,
    example: "EL123456789",
    description: "Greece: 9 digits",
  },
  HU: {
    pattern: /^HU\d{8}$/,
    example: "HU12345678",
    description: "Hungary: 8 digits",
  },
  IE: {
    pattern: /^IE\d{7}[A-Z]{1,2}$/,
    example: "IE1234567X",
    description: "Ireland: 7 digits + 1 or 2 letters",
  },
  IT: {
    pattern: /^IT\d{11}$/,
    example: "IT12345678901",
    description: "Italy: 11 digits",
  },
  LV: {
    pattern: /^LV\d{11}$/,
    example: "LV12345678901",
    description: "Latvia: 11 digits",
  },
  LT: {
    pattern: /^LT\d{9,12}$/,
    example: "LT123456789",
    description: "Lithuania: 9 or 12 digits",
  },
  LU: {
    pattern: /^LU\d{8}$/,
    example: "LU12345678",
    description: "Luxembourg: 8 digits",
  },
  MT: {
    pattern: /^MT\d{8}$/,
    example: "MT12345678",
    description: "Malta: 8 digits",
  },
  NL: {
    pattern: /^NL\d{9}B\d{2}$/,
    example: "NL123456789B12",
    description: "Netherlands: 9 digits + B + 2 digits",
  },
  PL: {
    pattern: /^PL\d{10}$/,
    example: "PL1234567890",
    description: "Poland: 10 digits",
  },
  PT: {
    pattern: /^PT\d{9}$/,
    example: "PT123456789",
    description: "Portugal: 9 digits",
  },
  RO: {
    pattern: /^RO\d{2,10}$/,
    example: "RO12345678",
    description: "Romania: 2 to 10 digits",
  },
  SK: {
    pattern: /^SK\d{10}$/,
    example: "SK1234567890",
    description: "Slovakia: 10 digits",
  },
  SI: {
    pattern: /^SI\d{8}$/,
    example: "SI12345678",
    description: "Slovenia: 8 digits",
  },
  ES: {
    pattern: /^ES[0-9A-Z]\d{7}[0-9A-Z]$/,
    example: "ESX1234567X",
    description: "Spain: 1 letter/num + 7 digits + 1 letter/num",
  },
  SE: {
    pattern: /^SE\d{10}01$/,
    example: "SE123456789001",
    description: "Sweden: 10 digits + 01",
  },

  // Non-EU European Countries
  GB: {
    pattern: /^GB(?:\d{9}|\d{12}|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    example: "GB123456789",
    description:
      "United Kingdom: 9 or 12 digits, or GD000-GD499, or HA500-HA999",
  },
  CH: {
    pattern: /^CHE\d{9}(MWST|TVA|IVA)$/,
    example: "CHE123456789MWST",
    description: "Switzerland: CHE + 9 digits + MWST/TVA/IVA",
  },
  NO: {
    pattern: /^NO\d{9}MVA$/,
    example: "NO123456789MVA",
    description: "Norway: 9 digits + MVA",
  },
  IS: {
    pattern: /^IS\d{6}$/,
    example: "IS123456",
    description: "Iceland: 6 digits",
  },
  LI: {
    pattern: /^LI\d{5}$/,
    example: "LI12345",
    description: "Liechtenstein: 5 digits",
  },
  MC: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Monaco: Same as France",
  },
  SM: {
    pattern: /^SM\d{5}$/,
    example: "SM12345",
    description: "San Marino: 5 digits",
  },
  VA: {
    pattern: /^IT\d{11}$/,
    example: "IT12345678901",
    description: "Vatican City: Same as Italy",
  },

  // Rest of World (General Tax ID patterns where VAT is not applicable)
  AF: {
    pattern: /^AF\d{1,15}$/,
    example: "AF1234567890",
    description: "Afghanistan: 1-15 digits",
  },
  AL: {
    pattern: /^AL\d{8}[A-Z]$/,
    example: "AL12345678A",
    description: "Albania: 8 digits + 1 letter",
  },
  DZ: {
    pattern: /^DZ\d{15}$/,
    example: "DZ123456789012345",
    description: "Algeria: 15 digits",
  },
  AD: {
    pattern: /^AD\d{8}[A-Z0-9]$/,
    example: "AD1234567A",
    description: "Andorra: 8 alphanumeric characters",
  },
  AO: {
    pattern: /^AO\d{9}$/,
    example: "AO123456789",
    description: "Angola: 9 digits",
  },
  AG: {
    pattern: /^AG\d{9}$/,
    example: "AG123456789",
    description: "Antigua and Barbuda: 9 digits",
  },
  AR: {
    pattern: /^AR\d{11}$/,
    example: "AR12345678901",
    description: "Argentina: 11 digits",
  },
  AM: {
    pattern: /^AM\d{8}$/,
    example: "AM12345678",
    description: "Armenia: 8 digits",
  },
  AU: {
    pattern: /^AU\d{9}$/,
    example: "AU123456789",
    description: "Australia: 9 digits (ABN)",
  },
  AZ: {
    pattern: /^AZ\d{9}$/,
    example: "AZ123456789",
    description: "Azerbaijan: 9 digits",
  },
  BS: {
    pattern: /^BS\d{9}$/,
    example: "BS123456789",
    description: "Bahamas: 9 digits",
  },
  BH: {
    pattern: /^BH\d{7,8}$/,
    example: "BH1234567",
    description: "Bahrain: 7 or 8 digits",
  },
  BD: {
    pattern: /^BD\d{10,12}$/,
    example: "BD1234567890",
    description: "Bangladesh: 10-12 digits",
  },
  BB: {
    pattern: /^BB\d{7,8}$/,
    example: "BB1234567",
    description: "Barbados: 7 or 8 digits",
  },
  BY: {
    pattern: /^BY\d{9}$/,
    example: "BY123456789",
    description: "Belarus: 9 digits",
  },
  BZ: {
    pattern: /^BZ\d{6,9}$/,
    example: "BZ123456",
    description: "Belize: 6-9 digits",
  },
  BJ: {
    pattern: /^BJ\d{8,12}$/,
    example: "BJ12345678",
    description: "Benin: 8-12 digits",
  },
  BT: {
    pattern: /^BT\d{7,11}$/,
    example: "BT1234567",
    description: "Bhutan: 7-11 digits",
  },
  BO: {
    pattern: /^BO\d{7,10}$/,
    example: "BO1234567",
    description: "Bolivia: 7-10 digits",
  },
  BA: {
    pattern: /^BA\d{10}$/,
    example: "BA1234567890",
    description: "Bosnia and Herzegovina: 10 digits",
  },
  BW: {
    pattern: /^BW\d{7,9}$/,
    example: "BW1234567",
    description: "Botswana: 7-9 digits",
  },
  BR: {
    pattern: /^BR\d{14,15}$/,
    example: "BR12345678901234",
    description: "Brazil: 14 or 15 digits (CNPJ/CPF)",
  },
  BN: {
    pattern: /^BN\d{10,12}$/,
    example: "BN1234567890",
    description: "Brunei: 10-12 digits",
  },
  BF: {
    pattern: /^BF\d{8,12}$/,
    example: "BF12345678",
    description: "Burkina Faso: 8-12 digits",
  },
  BI: {
    pattern: /^BI\d{8,10}$/,
    example: "BI12345678",
    description: "Burundi: 8-10 digits",
  },
  CV: {
    pattern: /^CV\d{8}$/,
    example: "CV12345678",
    description: "Cabo Verde: 8 digits",
  },
  KH: {
    pattern: /^KH\d{9,10}$/,
    example: "KH123456789",
    description: "Cambodia: 9 or 10 digits",
  },
  CM: {
    pattern: /^CM\d{8,10}$/,
    example: "CM12345678",
    description: "Cameroon: 8-10 digits",
  },
  CA: {
    pattern:
      /^CA\d{9}(RT|RP|RR|RC|RM|RN|RO|RP|RQ|RR|RS|RT|RU|RV|RW|RX|RY|RZ|SA|SB|SC|SD|SE|SF|SG|SH|SJ|SK|SL|SM|SN|SO|SP|SQ|SR|SS|ST|SU|SV|SW|SX|SY|SZ|T[0-9]|U[0-9]|V[0-9]|W[0-9]|X[0-9]|Y[0-9]|Z[0-9])$/,
    example: "CA123456789RT0001",
    description: "Canada: 9 digits + 2 letters + 4 digits",
  },
  CF: {
    pattern: /^CF\d{8,12}$/,
    example: "CF12345678",
    description: "Central African Republic: 8-12 digits",
  },
  TD: {
    pattern: /^TD\d{8,10}$/,
    example: "TD12345678",
    description: "Chad: 8-10 digits",
  },
  CL: {
    pattern: /^CL\d{8,9}[-\s]?[0-9K]$/,
    example: "CL123456789",
    description: "Chile: 8-9 digits + optional check digit",
  },
  CN: {
    pattern: /^[A-Z0-9]{15,18}$/,
    example: "91310000710936658K",
    description: "China: 15-18 alphanumeric characters",
  },
  CO: {
    pattern: /^CO\d{10,15}$/,
    example: "CO1234567890",
    description: "Colombia: 10-15 digits",
  },
  KM: {
    pattern: /^KM\d{8,10}$/,
    example: "KM12345678",
    description: "Comoros: 8-10 digits",
  },
  CG: {
    pattern: /^CG\d{8,12}$/,
    example: "CG12345678",
    description: "Congo: 8-12 digits",
  },
  CD: {
    pattern: /^CD\d{10,12}$/,
    example: "CD1234567890",
    description: "DR Congo: 10-12 digits",
  },
  CR: {
    pattern: /^CR\d{9,12}$/,
    example: "CR123456789",
    description: "Costa Rica: 9-12 digits",
  },
  CI: {
    pattern: /^CI\d{8,12}$/,
    example: "CI12345678",
    description: "Côte d'Ivoire: 8-12 digits",
  },
  CU: {
    pattern: /^CU\d{11}$/,
    example: "CU12345678901",
    description: "Cuba: 11 digits",
  },
  DJ: {
    pattern: /^DJ\d{8,10}$/,
    example: "DJ12345678",
    description: "Djibouti: 8-10 digits",
  },
  DM: {
    pattern: /^DM\d{7,9}$/,
    example: "DM1234567",
    description: "Dominica: 7-9 digits",
  },
  DO: {
    pattern: /^DO\d{9,11}$/,
    example: "DO123456789",
    description: "Dominican Republic: 9-11 digits",
  },
  EC: {
    pattern: /^EC\d{13}$/,
    example: "EC1234567890123",
    description: "Ecuador: 13 digits",
  },
  EG: {
    pattern: /^EG\d{9,15}$/,
    example: "EG123456789",
    description: "Egypt: 9-15 digits",
  },
  SV: {
    pattern: /^SV\d{8,10}$/,
    example: "SV12345678",
    description: "El Salvador: 8-10 digits",
  },
  GQ: {
    pattern: /^GQ\d{7,9}$/,
    example: "GQ1234567",
    description: "Equatorial Guinea: 7-9 digits",
  },
  ER: {
    pattern: /^ER\d{8,10}$/,
    example: "ER12345678",
    description: "Eritrea: 8-10 digits",
  },
  SZ: {
    pattern: /^SZ\d{7,9}$/,
    example: "SZ1234567",
    description: "Eswatini: 7-9 digits",
  },
  ET: {
    pattern: /^ET\d{10,15}$/,
    example: "ET1234567890",
    description: "Ethiopia: 10-15 digits",
  },
  FJ: {
    pattern: /^FJ\d{5,8}$/,
    example: "FJ12345",
    description: "Fiji: 5-8 digits",
  },
  GA: {
    pattern: /^GA\d{8,10}$/,
    example: "GA12345678",
    description: "Gabon: 8-10 digits",
  },
  GM: {
    pattern: /^GM\d{8,10}$/,
    example: "GM12345678",
    description: "Gambia: 8-10 digits",
  },
  GE: {
    pattern: /^GE\d{9,12}$/,
    example: "GE123456789",
    description: "Georgia: 9-12 digits",
  },
  GH: {
    pattern: /^GH\d{10,15}$/,
    example: "GH1234567890",
    description: "Ghana: 10-15 digits",
  },
  GD: {
    pattern: /^GD\d{7,9}$/,
    example: "GD1234567",
    description: "Grenada: 7-9 digits",
  },
  GT: {
    pattern: /^GT\d{7,10}$/,
    example: "GT1234567",
    description: "Guatemala: 7-10 digits",
  },
  GN: {
    pattern: /^GN\d{8,12}$/,
    example: "GN12345678",
    description: "Guinea: 8-12 digits",
  },
  GW: {
    pattern: /^GW\d{8,10}$/,
    example: "GW12345678",
    description: "Guinea-Bissau: 8-10 digits",
  },
  GY: {
    pattern: /^GY\d{7,9}$/,
    example: "GY1234567",
    description: "Guyana: 7-9 digits",
  },
  HT: {
    pattern: /^HT\d{8,10}$/,
    example: "HT12345678",
    description: "Haiti: 8-10 digits",
  },
  HN: {
    pattern: /^HN\d{10,15}$/,
    example: "HN1234567890",
    description: "Honduras: 10-15 digits",
  },
  HK: {
    pattern: /^[A-Z]{1,2}\d{6,8}\([0-9A-Za-z]\/?$/,
    example: "D123456(7)",
    description: "Hong Kong: 1-2 letters + 6-8 digits + check digit",
  },
  IN: {
    pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    example: "12ABCDE3456F7Z8",
    description: "India: 15-digit alphanumeric GSTIN",
  },
  ID: {
    pattern: /^[0-9]{15,16}$/,
    example: "123456789012345",
    description: "Indonesia: 15 or 16 digits (NPWP)",
  },
  IR: {
    pattern: /^IR\d{10,12}$/,
    example: "IR1234567890",
    description: "Iran: 10-12 digits",
  },
  IQ: {
    pattern: /^IQ\d{9,12}$/,
    example: "IQ123456789",
    description: "Iraq: 9-12 digits",
  },
  IL: {
    pattern: /^IL\d{9}$/,
    example: "IL123456789",
    description: "Israel: 9 digits",
  },
  JM: {
    pattern: /^JM\d{8,10}$/,
    example: "JM12345678",
    description: "Jamaica: 8-10 digits",
  },
  JP: {
    pattern: /^[0-9]{13}$/,
    example: "1234567890123",
    description: "Japan: 13 digits",
  },
  JO: {
    pattern: /^JO\d{9,12}$/,
    example: "JO123456789",
    description: "Jordan: 9-12 digits",
  },
  KZ: {
    pattern: /^KZ\d{12}$/,
    example: "KZ123456789012",
    description: "Kazakhstan: 12 digits",
  },
  KE: {
    pattern: /^[A-Z]{1}[0-9]{9}[A-Z]{1}$/,
    example: "A123456789Z",
    description: "Kenya: 1 letter + 9 digits + 1 letter",
  },
  KI: {
    pattern: /^KI\d{8,10}$/,
    example: "KI12345678",
    description: "Kiribati: 8-10 digits",
  },
  KP: {
    pattern: /^KP\d{8,12}$/,
    example: "KP12345678",
    description: "Korea, North: 8-12 digits",
  },
  KR: {
    pattern: /^[0-9]{10}$/,
    example: "1234567890",
    description: "Korea, South: 10 digits",
  },
  XK: {
    pattern: /^XK\d{8,10}$/,
    example: "XK12345678",
    description: "Kosovo: 8-10 digits",
  },
  KW: {
    pattern: /^KW\d{7,9}$/,
    example: "KW1234567",
    description: "Kuwait: 7-9 digits",
  },
  KG: {
    pattern: /^KG\d{8,10}$/,
    example: "KG12345678",
    description: "Kyrgyzstan: 8-10 digits",
  },
  LA: {
    pattern: /^LA\d{10,12}$/,
    example: "LA1234567890",
    description: "Laos: 10-12 digits",
  },
  LB: {
    pattern: /^LB\d{8,10}$/,
    example: "LB12345678",
    description: "Lebanon: 8-10 digits",
  },
  LS: {
    pattern: /^LS\d{8,10}$/,
    example: "LS12345678",
    description: "Lesotho: 8-10 digits",
  },
  LR: {
    pattern: /^LR\d{8,10}$/,
    example: "LR12345678",
    description: "Liberia: 8-10 digits",
  },
  LY: {
    pattern: /^LY\d{9,12}$/,
    example: "LY123456789",
    description: "Libya: 9-12 digits",
  },
  MO: {
    pattern: /^[0-9]{8}$/,
    example: "12345678",
    description: "Macao: 8 digits",
  },
  MG: {
    pattern: /^MG\d{8,10}$/,
    example: "MG12345678",
    description: "Madagascar: 8-10 digits",
  },
  MW: {
    pattern: /^MW\d{8,10}$/,
    example: "MW12345678",
    description: "Malawi: 8-10 digits",
  },
  MY: {
    pattern: /^[0-9]{10,12}$/,
    example: "123456789012",
    description: "Malaysia: 10-12 digits (SST number)",
  },
  MV: {
    pattern: /^MV\d{7,9}$/,
    example: "MV1234567",
    description: "Maldives: 7-9 digits",
  },
  ML: {
    pattern: /^ML\d{8,10}$/,
    example: "ML12345678",
    description: "Mali: 8-10 digits",
  },
  MH: {
    pattern: /^MH\d{7,9}$/,
    example: "MH1234567",
    description: "Marshall Islands: 7-9 digits",
  },
  MR: {
    pattern: /^MR\d{8,10}$/,
    example: "MR12345678",
    description: "Mauritania: 8-10 digits",
  },
  MU: {
    pattern: /^MU\d{7,9}$/,
    example: "MU1234567",
    description: "Mauritius: 7-9 digits",
  },
  MX: {
    pattern:
      /^[A-Z&Ñ]{3,4}[0-9]{2}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])[A-Z0-9]{2}[0-9A]$/,
    example: "ABC123456T1B",
    description: "Mexico: RFC (12-13 alphanumeric characters)",
  },
  FM: {
    pattern: /^FM\d{7,9}$/,
    example: "FM1234567",
    description: "Micronesia: 7-9 digits",
  },
  MD: {
    pattern: /^MD\d{8,10}$/,
    example: "MD12345678",
    description: "Moldova: 8-10 digits",
  },
  MN: {
    pattern: /^MN\d{7,9}$/,
    example: "MN1234567",
    description: "Mongolia: 7-9 digits",
  },
  ME: {
    pattern: /^ME\d{8,10}$/,
    example: "ME12345678",
    description: "Montenegro: 8-10 digits",
  },
  MA: {
    pattern: /^MA\d{8,10}$/,
    example: "MA12345678",
    description: "Morocco: 8-10 digits",
  },
  MZ: {
    pattern: /^MZ\d{9,12}$/,
    example: "MZ123456789",
    description: "Mozambique: 9-12 digits",
  },
  MM: {
    pattern: /^MM\d{9,12}$/,
    example: "MM123456789",
    description: "Myanmar: 9-12 digits",
  },
  NA: {
    pattern: /^NA\d{7,9}$/,
    example: "NA1234567",
    description: "Namibia: 7-9 digits",
  },
  NR: {
    pattern: /^NR\d{7,9}$/,
    example: "NR1234567",
    description: "Nauru: 7-9 digits",
  },
  NP: {
    pattern: /^NP\d{9,11}$/,
    example: "NP123456789",
    description: "Nepal: 9-11 digits",
  },
  NZ: {
    pattern: /^[0-9]{8,10}$/,
    example: "123456789",
    description: "New Zealand: 8-10 digits (IRD number)",
  },
  NI: {
    pattern: /^NI\d{8,10}$/,
    example: "NI12345678",
    description: "Nicaragua: 8-10 digits",
  },
  NE: {
    pattern: /^NE\d{8,10}$/,
    example: "NE12345678",
    description: "Niger: 8-10 digits",
  },
  NG: {
    pattern: /^NG\d{10,12}$/,
    example: "NG1234567890",
    description: "Nigeria: 10-12 digits",
  },
  MK: {
    pattern: /^MK\d{13}$/,
    example: "MK1234567890123",
    description: "North Macedonia: 13 digits",
  },
  PK: {
    pattern: /^[0-9]{7}$/,
    example: "1234567",
    description: "Pakistan: 7 digits (NTN)",
  },
  PA: {
    pattern: /^[0-9]{9,10}$/,
    example: "123456789",
    description: "Panama: 9 or 10 digits (RUC)",
  },
  PG: {
    pattern: /^PG\d{8,10}$/,
    example: "PG12345678",
    description: "Papua New Guinea: 8-10 digits",
  },
  PY: {
    pattern: /^[0-9]{6,8}-[0-9]$/,
    example: "1234567-8",
    description: "Paraguay: 6-8 digits + check digit",
  },
  PE: {
    pattern: /^[0-9]{11}$/,
    example: "12345678901",
    description: "Peru: 11 digits (RUC)",
  },
  PH: {
    pattern: /^[0-9]{3}-[0-9]{3}-[0-9]{3}-[0-9]{3}$/,
    example: "123-456-789-123",
    description: "Philippines: 12 digits in 3-3-3-3 format",
  },
  QA: {
    pattern: /^QA\d{8,10}$/,
    example: "QA12345678",
    description: "Qatar: 8-10 digits",
  },
  RU: {
    pattern: /^[0-9]{10,12}$/,
    example: "1234567890",
    description: "Russia: 10 or 12 digits (INN)",
  },
  RW: {
    pattern: /^RW\d{8,10}$/,
    example: "RW12345678",
    description: "Rwanda: 8-10 digits",
  },
  KN: {
    pattern: /^KN\d{7,9}$/,
    example: "KN1234567",
    description: "Saint Kitts and Nevis: 7-9 digits",
  },
  LC: {
    pattern: /^LC\d{7,9}$/,
    example: "LC1234567",
    description: "Saint Lucia: 7-9 digits",
  },
  VC: {
    pattern: /^VC\d{7,9}$/,
    example: "VC1234567",
    description: "Saint Vincent and the Grenadines: 7-9 digits",
  },
  WS: {
    pattern: /^WS\d{7,9}$/,
    example: "WS1234567",
    description: "Samoa: 7-9 digits",
  },
  ST: {
    pattern: /^ST\d{8,10}$/,
    example: "ST12345678",
    description: "Sao Tome and Principe: 8-10 digits",
  },
  SA: {
    pattern: /^[0-9]{15}$/,
    example: "123456789012345",
    description: "Saudi Arabia: 15 digits",
  },
  SN: {
    pattern: /^SN\d{9,12}$/,
    example: "SN123456789",
    description: "Senegal: 9-12 digits",
  },
  RS: {
    pattern: /^[0-9]{9}$/,
    example: "123456789",
    description: "Serbia: 9 digits (PIB)",
  },
  SC: {
    pattern: /^SC\d{8,10}$/,
    example: "SC12345678",
    description: "Seychelles: 8-10 digits",
  },
  SL: {
    pattern: /^SL\d{8,10}$/,
    example: "SL12345678",
    description: "Sierra Leone: 8-10 digits",
  },
  SG: {
    pattern: /^[0-9]{9,10}[A-Za-z]?$/,
    example: "123456789",
    description: "Singapore: 9-10 digits + optional letter",
  },
  SB: {
    pattern: /^SB\d{7,9}$/,
    example: "SB1234567",
    description: "Solomon Islands: 7-9 digits",
  },
  SO: {
    pattern: /^SO\d{8,10}$/,
    example: "SO12345678",
    description: "Somalia: 8-10 digits",
  },
  ZA: {
    pattern: /^[0-9]{10}$/,
    example: "1234567890",
    description: "South Africa: 10 digits (Income Tax Number)",
  },
  SS: {
    pattern: /^SS\d{8,10}$/,
    example: "SS12345678",
    description: "South Sudan: 8-10 digits",
  },
  LK: {
    pattern: /^[0-9]{9,12}$/,
    example: "123456789",
    description: "Sri Lanka: 9-12 digits",
  },
  SD: {
    pattern: /^SD\d{9,12}$/,
    example: "SD123456789",
    description: "Sudan: 9-12 digits",
  },
  SR: {
    pattern: /^[0-9]{8,10}$/,
    example: "12345678",
    description: "Suriname: 8-10 digits",
  },
  SY: {
    pattern: /^SY\d{9,12}$/,
    example: "SY123456789",
    description: "Syria: 9-12 digits",
  },
  TW: {
    pattern: /^[0-9]{8}$/,
    example: "12345678",
    description: "Taiwan: 8 digits",
  },
  TJ: {
    pattern: /^TJ\d{9,12}$/,
    example: "TJ123456789",
    description: "Tajikistan: 9-12 digits",
  },
  TZ: {
    pattern: /^[0-9]{9,12}$/,
    example: "123456789",
    description: "Tanzania: 9-12 digits",
  },
  TH: {
    pattern: /^[0-9]{13}$/,
    example: "1234567890123",
    description: "Thailand: 13 digits",
  },
  TL: {
    pattern: /^TL\d{8,10}$/,
    example: "TL12345678",
    description: "Timor-Leste: 8-10 digits",
  },
  TG: {
    pattern: /^TG\d{8,10}$/,
    example: "TG12345678",
    description: "Togo: 8-10 digits",
  },
  TO: {
    pattern: /^TO\d{7,9}$/,
    example: "TO1234567",
    description: "Tonga: 7-9 digits",
  },
  TT: {
    pattern: /^TT\d{8,10}$/,
    example: "TT12345678",
    description: "Trinidad and Tobago: 8-10 digits",
  },
  TN: {
    pattern: /^[0-9]{8}$/,
    example: "12345678",
    description: "Tunisia: 8 digits",
  },
  TR: {
    pattern: /^[0-9]{10}$/,
    example: "1234567890",
    description: "Turkey: 10 digits",
  },
  TM: {
    pattern: /^TM\d{8,10}$/,
    example: "TM12345678",
    description: "Turkmenistan: 8-10 digits",
  },
  TV: {
    pattern: /^TV\d{7,9}$/,
    example: "TV1234567",
    description: "Tuvalu: 7-9 digits",
  },
  UG: {
    pattern: /^[0-9]{10,12}$/,
    example: "1234567890",
    description: "Uganda: 10-12 digits",
  },
  UA: {
    pattern: /^[0-9]{10,12}$/,
    example: "1234567890",
    description: "Ukraine: 10 or 12 digits",
  },
  AE: {
    pattern: /^[0-9]{15}$/,
    example: "123456789012345",
    description: "United Arab Emirates: 15 digits",
  },
  US: {
    pattern: /^[0-9]{2}-[0-9]{7}$/,
    example: "12-3456789",
    description: "United States: 9 digits (EIN)",
  },
  UY: {
    pattern: /^[0-9]{12}$/,
    example: "123456789012",
    description: "Uruguay: 12 digits (RUT)",
  },
  UZ: {
    pattern: /^[0-9]{9,12}$/,
    example: "123456789",
    description: "Uzbekistan: 9-12 digits",
  },
  VU: {
    pattern: /^VU\d{7,9}$/,
    example: "VU1234567",
    description: "Vanuatu: 7-9 digits",
  },
  VE: {
    pattern: /^[VEJPG][0-9]{9}$/,
    example: "V123456789",
    description: "Venezuela: 1 letter + 9 digits",
  },
  VN: {
    pattern: /^[0-9]{10,14}$/,
    example: "1234567890",
    description: "Vietnam: 10-14 digits",
  },
  YE: {
    pattern: /^YE\d{9,12}$/,
    example: "YE123456789",
    description: "Yemen: 9-12 digits",
  },
  ZM: {
    pattern: /^ZM\d{9,12}$/,
    example: "ZM123456789",
    description: "Zambia: 9-12 digits",
  },
  ZW: {
    pattern: /^ZW\d{8,12}$/,
    example: "ZW12345678",
    description: "Zimbabwe: 8-12 digits",
  },

  // Special Cases and Dependencies
  AX: {
    pattern: /^FI\d{8}$/,
    example: "FI12345678",
    description: "Åland Islands: Same as Finland",
  },
  BL: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Saint Barthélemy: Same as France",
  },
  BQ: {
    pattern: /^NL\d{9}B\d{2}$/,
    example: "NL123456789B12",
    description: "Bonaire, Sint Eustatius and Saba: Same as Netherlands",
  },
  CW: {
    pattern: /^[0-9]{8,10}$/,
    example: "12345678",
    description: "Curaçao: 8-10 digits",
  },
  GF: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "French Guiana: Same as France",
  },
  PF: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "French Polynesia: Same as France",
  },
  TF: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "French Southern Territories: Same as France",
  },
  GP: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Guadeloupe: Same as France",
  },
  GU: {
    pattern: /^[0-9]{9}$/,
    example: "123456789",
    description: "Guam: 9 digits (US format)",
  },
  GG: {
    pattern: /^GB(?:\d{9}|\d{12}|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    example: "GB123456789",
    description: "Guernsey: Same as UK",
  },
  IM: {
    pattern: /^GB(?:\d{9}|\d{12}|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    example: "GB123456789",
    description: "Isle of Man: Same as UK",
  },
  JE: {
    pattern: /^GB(?:\d{9}|\d{12}|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    example: "GB123456789",
    description: "Jersey: Same as UK",
  },
  MQ: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Martinique: Same as France",
  },
  YT: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Mayotte: Same as France",
  },
  NC: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "New Caledonia: Same as France",
  },
  PR: {
    pattern: /^[0-9]{7}$/,
    example: "1234567",
    description: "Puerto Rico: 7 digits",
  },
  RE: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Réunion: Same as France",
  },
  MF: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Saint Martin: Same as France",
  },
  PM: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Saint Pierre and Miquelon: Same as France",
  },
  SX: {
    pattern: /^[0-9]{8,10}$/,
    example: "12345678",
    description: "Sint Maarten: 8-10 digits",
  },
  GS: {
    pattern: /^GB(?:\d{9}|\d{12}|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    example: "GB123456789",
    description: "South Georgia: Same as UK",
  },
  PS: {
    pattern: /^PS\d{9,12}$/,
    example: "PS123456789",
    description: "Palestine: 9-12 digits",
  },
  EH: {
    pattern: /^[0-9]{8,10}$/,
    example: "12345678",
    description: "Western Sahara: 8-10 digits",
  },
  WF: {
    pattern: /^FR[0-9A-Z]{2}\d{9}$/,
    example: "FRXX123456789",
    description: "Wallis and Futuna: Same as France",
  },
  IO: {
    pattern: /^GB(?:\d{9}|\d{12}|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    example: "GB123456789",
    description: "British Indian Ocean Territory: Same as UK",
  },
  VG: {
    pattern: /^VG\d{8,10}$/,
    example: "VG12345678",
    description: "British Virgin Islands: 8-10 digits",
  },
  VI: {
    pattern: /^[0-9]{9}$/,
    example: "123456789",
    description: "US Virgin Islands: 9 digits (US format)",
  },
  UM: {
    pattern: /^[0-9]{9}$/,
    example: "123456789",
    description: "US Minor Outlying Islands: 9 digits (US format)",
  },
  AS: {
    pattern: /^[0-9]{9}$/,
    example: "123456789",
    description: "American Samoa: 9 digits (US format)",
  },
  MP: {
    pattern: /^[0-9]{9}$/,
    example: "123456789",
    description: "Northern Mariana Islands: 9 digits (US format)",
  },
  GL: {
    pattern: /^\d{6}-?\d{4}$/,
    example: "123456-7890",
    description: "Greenland: 10 digits (6+4 with optional dash)",
  },
  FO: {
    pattern: /^\d{6}-?\d{4}$/,
    example: "123456-7890",
    description: "Faroe Islands: 10 digits (6+4 with optional dash)",
  },
  SJ: {
    pattern: /^NO\d{9}MVA$/,
    example: "NO123456789MVA",
    description: "Svalbard and Jan Mayen: Same as Norway",
  },
  TK: {
    pattern: /^[0-9]{4,8}$/,
    example: "12345",
    description: "Tokelau: 4-8 digits",
  },
  NU: {
    pattern: /^[0-9]{4,8}$/,
    example: "12345",
    description: "Niue: 4-8 digits",
  },
  NF: {
    pattern: /^[0-9]{6,9}$/,
    example: "123456",
    description: "Norfolk Island: 6-9 digits",
  },
  PN: {
    pattern: /^[0-9]{4,8}$/,
    example: "12345",
    description: "Pitcairn: 4-8 digits",
  },
  SH: {
    pattern: /^[0-9]{6,9}$/,
    example: "123456",
    description: "Saint Helena: 6-9 digits",
  },
  TC: {
    pattern: /^[0-9]{7,9}$/,
    example: "1234567",
    description: "Turks and Caicos: 7-9 digits",
  },
};
